import React, { useState } from 'react';
import { Card, Alert, Table, Form, Button, Badge, InputGroup, Modal } from 'react-bootstrap';
import { FaUtensils, FaPlus, FaTimes, FaTrash, FaUser, FaEdit, FaRupeeSign, FaInfoCircle } from 'react-icons/fa';
import { colors } from '../../../../../utils/Colors';

const MenuSelection = ({
  formData,
  setShowMenuModal,
  handleMenuChange,
  removeMenuItem,
  addCustomMenuItem,
  formatCurrency,
  calculateFoodBill,
  calculatePerHead,
  updateCalculations,
  setFormData
}) => {
  const [editPerHead, setEditPerHead] = useState(false);
  const [tempPerHead, setTempPerHead] = useState(formData.mainMenuPrice.toString());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemText, setEditingItemText] = useState('');

  // Add extra item to selected main menu
  const addExtraItemToMenu = () => {
    const newItem = { 
      item: '', 
      price: 0,
      category: 'Extra Item',
      isExtraItem: true 
    };

    setFormData(prev => ({
      ...prev,
      menu: [...prev.menu, newItem]
    }));
  };

  // Remove extra item
  const removeExtraItem = (index) => {
    const updatedMenu = formData.menu.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      menu: updatedMenu
    }));
  };

  // Handle extra item change
  const handleExtraItemChange = (index, field, value) => {
    const updatedMenu = [...formData.menu];
    updatedMenu[index] = {
      ...updatedMenu[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      menu: updatedMenu
    }));
  };

  // Handle per head price change
  const handlePerHeadChange = (e) => {
    const value = e.target.value.replace(/\./g, '');
    setTempPerHead(value);
  };

  // Save per head price
  const savePerHeadPrice = () => {
    const perHeadValue = parseFloat(tempPerHead) || formData.mainMenuPrice;
    
    setFormData(prev => ({
      ...prev,
      mainMenuPrice: perHeadValue
    }));
    
    setEditPerHead(false);
    setTimeout(() => updateCalculations(), 100);
  };

  // Get display per head price
  const getDisplayPerHead = () => {
    return formData.mainMenuPrice;
  };

  // Edit main menu item
  const startEditingItem = (index, itemText) => {
    setEditingItemIndex(index);
    setEditingItemText(itemText);
    setShowEditModal(true);
  };

  // Save edited main menu item
  const saveEditedItem = () => {
    if (editingItemIndex !== null) {
      const updatedMainMenuItems = [...formData.mainMenuItems];
      updatedMainMenuItems[editingItemIndex] = editingItemText;
      
      setFormData(prev => ({
        ...prev,
        mainMenuItems: updatedMainMenuItems
      }));
      
      setShowEditModal(false);
      setEditingItemIndex(null);
      setEditingItemText('');
    }
  };

  // Delete main menu item
  const deleteMainMenuItem = (index) => {
    const updatedMainMenuItems = formData.mainMenuItems.filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      mainMenuItems: updatedMainMenuItems
    }));
  };

  // Add new item to main menu
  const addNewItemToMainMenu = () => {
    setEditingItemIndex(null);
    setEditingItemText('');
    setShowEditModal(true);
  };

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: colors.TABLE_HEADERS }}>
        <div className="d-flex align-items-center">
          <div className="p-2 me-3 bg-white bg-opacity-25 rounded-circle">
            <FaUtensils size={20} />
          </div>
          <h5 className="mb-0">Menu Selection</h5>
        </div>
        <Button
          variant="light"
          size="sm"
          onClick={() => setShowMenuModal(true)}
        >
          <FaPlus className="me-1" />
          Select Menu
        </Button>
      </Card.Header>
      <Card.Body>
        {/* Main Menu Selection Section */}
        {formData.selectedMainMenu ? (
          <div className="mb-4">
            <Alert variant="info">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <FaUtensils className="me-2" />
                  <strong>Selected Menu:</strong> {formData.selectedMainMenu}
                </div>
                
                {/* Per Head Price Editor */}
                <div className="d-flex align-items-center gap-2">
                  {editPerHead ? (
                    <div className="d-flex align-items-center gap-2">
                      <InputGroup size="sm" style={{ width: '180px' }}>
                        <InputGroup.Text>
                          <div>RS</div>
                        </InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={tempPerHead}
                          onChange={handlePerHeadChange}
                          placeholder="Per head amount"
                          className="fw-bold"
                        />
                        <InputGroup.Text>per person</InputGroup.Text>
                      </InputGroup>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={savePerHeadPrice}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setEditPerHead(false);
                          setTempPerHead(formData.mainMenuPrice.toString());
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Badge bg="info" className="fs-6">
                        <FaUser className="me-1" />
                        {formatCurrency(getDisplayPerHead())} per person
                      </Badge>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => {
                          setEditPerHead(true);
                          setTempPerHead(formData.mainMenuPrice.toString());
                        }}
                      >
                        <FaEdit className="me-1" />
                        Edit Price
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Menu Items:</strong>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={addNewItemToMainMenu}
                  >
                    <FaPlus className="me-1" />
                    Add New Item
                  </Button>
                </div>
                <div className="mt-2">
                  {formData.mainMenuItems.map((item, idx) => (
                    <div key={idx} className="d-flex align-items-center justify-content-between bg-light p-2 mb-2 rounded">
                      <div className="d-flex align-items-center">
                        <span className="me-3 text-muted">#{idx + 1}</span>
                        <span>{item}</span>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => startEditingItem(idx, item)}
                          className="rounded-circle"
                          style={{ width: '30px', height: '30px' }}
                        >
                          <FaEdit size={12} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteMainMenuItem(idx)}
                          className="rounded-circle"
                          style={{ width: '30px', height: '30px' }}
                        >
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {formData.mainMenuItems.length === 0 && (
                    <Alert variant="light" className="text-center py-3">
                      No items in this menu. Add items using the button above.
                    </Alert>
                  )}
                </div>
              </div>

              <div className="alert alert-success mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Price Calculation:</strong>
                    <div className="mt-1">
                      PKR {getDisplayPerHead()} × {formData.eventDetails.persons || 0} persons = 
                      <span className="fw-bold ms-2">{formatCurrency(calculateFoodBill())}</span>
                    </div>
                    {formData.menu.length > 0 && (
                      <div className="mt-2 small text-muted">
                        <FaPlus className="me-1" />
                        Includes {formData.menu.length} extra item(s) in the per head price
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        selectedMainMenu: '',
                        mainMenuPrice: 0,
                        mainMenuItems: [],
                        menu: [] // Clear extra items too
                      }));
                      setTimeout(() => updateCalculations(), 100);
                    }}
                  >
                    <FaTimes className="me-1" />
                    Change Menu
                  </Button>
                </div>
              </div>
            </Alert>

            {/* Extra Items Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0 fw-bold">
                    <FaPlus className="me-2 text-success" />
                    Extra Items ({formData.menu.length})
                  </h6>
                  <small className="text-muted">
                    Extra items increase the per head value
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={addExtraItemToMenu}
                  >
                    <FaPlus className="me-1" />
                    Add Extra Item
                  </Button>
                  {formData.menu.length > 0 && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => setEditPerHead(true)}
                    >
                      <FaEdit className="me-1" />
                      Adjust Price
                    </Button>
                  )}
                </div>
              </div>
              
              <Alert variant="warning" className="mb-3">
                <div className="small">
                  <FaInfoCircle className="me-2" />
                  <strong>Note:</strong> When you add extra items, you should adjust the per head price accordingly.
                  Current per head price: <strong>{formatCurrency(getDisplayPerHead())}</strong>
                </div>
              </Alert>

              {/* Extra Items Table */}
              {formData.menu.length > 0 && (
                <div className="table-responsive mb-3">
                  <Table bordered hover size="sm">
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th>#</th>
                        <th>Extra Item Name</th>
                        <th>Type</th>
                        <th>Effect on Price</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.menu.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <Form.Control
                              type="text"
                              value={item.item}
                              onChange={(e) => handleExtraItemChange(index, 'item', e.target.value)}
                              placeholder="Extra item name"
                              size="sm"
                            />
                          </td>
                          <td>
                            <Badge bg="success">Extra Item</Badge>
                          </td>
                          <td className="text-center">
                            <Badge bg={editPerHead ? "warning" : "info"}>
                              {editPerHead ? "Adjust Price ↑" : `PKR ${getDisplayPerHead()}`}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeExtraItem(index)}
                              className="rounded-circle"
                              style={{ width: '30px', height: '30px' }}
                            >
                              <FaTrash size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <td colSpan="5" className="text-center small">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>
                              Extra items increase the value. Consider adjusting per head price from <strong>{formatCurrency(formData.mainMenuPrice)}</strong>
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setEditPerHead(true)}
                            >
                              <FaEdit className="me-1" />
                              Adjust Price Now
                            </Button>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* No Menu Selected State */
          <div className="text-center py-5">
            <FaUtensils size={48} className="text-muted mb-3" />
            <h5 className="text-muted mb-3">No Menu Selected</h5>
            <p className="text-muted mb-4">
              Select a main menu to get started. You can add extra items and adjust the per head price.
            </p>
            <Button
              variant="primary"
              onClick={() => setShowMenuModal(true)}
              size="lg"
            >
              <FaUtensils className="me-2" />
              Select Main Menu
            </Button>
          </div>
        )}

        {/* Custom Menu Section (Separate) - Only shown when NO main menu is selected */}
        {!formData.selectedMainMenu && formData.menu.length > 0 && (
          <Alert variant="warning" className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <FaUtensils className="me-2" />
                <strong>Custom Menu Items:</strong> {formData.menu.length} item(s)
              </div>
              <Badge bg="warning" className="fs-6">
                Custom Menu
              </Badge>
            </div>
            
            <div className="table-responsive mb-3">
              <Table bordered hover size="sm">
                <thead>
                  <tr style={{ backgroundColor: colors.TABLE_HEADERS, color: 'white' }}>
                    <th>#</th>
                    <th>Item Name</th>
                    <th>Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.menu.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <Form.Control
                          type="text"
                          value={item.item}
                          onChange={(e) => handleMenuChange(index, 'item', e.target.value)}
                          placeholder="Item name"
                          size="sm"
                        />
                      </td>
                      <td>
                        <Badge bg="secondary">{item.category || 'Custom'}</Badge>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeMenuItem(index)}
                          className="rounded-circle"
                          style={{ width: '30px', height: '30px' }}
                        >
                          <FaTrash size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    menu: []
                  }));
                }}
              >
                <FaTimes className="me-1" />
                Remove All Items
              </Button>
              
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addCustomMenuItem}
              >
                <FaPlus className="me-1" />
                Add More Items
              </Button>
            </div>
          </Alert>
        )}

        {/* Summary Section */}
        {(formData.selectedMainMenu || formData.menu.length > 0) && (
          <Card className="border-primary mt-4">
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <h6 className="fw-bold mb-3">Menu Summary</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Menu Type:</span>
                    <Badge bg={formData.selectedMainMenu ? "info" : "warning"}>
                      {formData.selectedMainMenu ? 'Main Menu + Extra Items' : 'Custom Menu'}
                    </Badge>
                  </div>
                  {formData.selectedMainMenu && (
                    <>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Menu Name:</span>
                        <span className="fw-bold">{formData.selectedMainMenu}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Base Items:</span>
                        <span>{formData.mainMenuItems.length} items</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Extra Items:</span>
                        <span className={formData.menu.length > 0 ? "text-success fw-bold" : ""}>
                          {formData.menu.length} items
                          {formData.menu.length > 0 && " (Price adjusted)"}
                        </span>
                      </div>
                    </>
                  )}
                  {!formData.selectedMainMenu && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Custom Items:</span>
                      <span>{formData.menu.length} items</span>
                    </div>
                  )}
                </div>
                
                <div className="col-md-6">
                  <h6 className="fw-bold mb-3">Price Summary</h6>
                  {formData.selectedMainMenu ? (
                    <>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Per Head Price:</span>
                        <span className="fw-bold d-flex align-items-center">
                          {formatCurrency(getDisplayPerHead())}
                          {formData.menu.length > 0 && (
                            <Badge bg="warning" className="ms-2">
                              Adjusted
                            </Badge>
                          )}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Number of Persons:</span>
                        <span>{formData.eventDetails.persons || 0}</span>
                      </div>
                      {formData.menu.length > 0 && (
                        <div className="d-flex justify-content-between mb-2 text-success">
                          <span>Extra Items Value:</span>
                          <span>Included in per head price</span>
                        </div>
                      )}
                      <hr />
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">Total Food Bill:</span>
                        <span className="fw-bold fs-5 text-success">
                          {formatCurrency(calculateFoodBill())}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="alert alert-warning">
                      <FaUser className="me-2" />
                      <strong>Custom Menu:</strong> Set per head amount in Event Details section
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="d-flex flex-wrap gap-2 mt-4">
          {formData.selectedMainMenu ? (
            <>
              <Button
                variant="outline-primary"
                onClick={addExtraItemToMenu}
                className="me-2"
              >
                <FaPlus className="me-2" />
                Add Extra Item
              </Button>
              <Button
                variant={formData.menu.length > 0 ? "warning" : "outline-warning"}
                onClick={() => setEditPerHead(true)}
                className="me-2"
              >
                <FaEdit className="me-2" />
                {formData.menu.length > 0 ? 'Adjust Price' : 'Edit Price'}
              </Button>
              <Button
                variant="outline-success"
                onClick={() => setShowMenuModal(true)}
              >
                <FaUtensils className="me-2" />
                Change Menu
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline-primary"
                onClick={addCustomMenuItem}
                className="me-2"
                disabled={formData.selectedMainMenu}
              >
                <FaPlus className="me-2" />
                Add Custom Item
              </Button>
              <Button
                variant="outline-success"
                onClick={() => setShowMenuModal(true)}
              >
                <FaUtensils className="me-2" />
                Select Main Menu
              </Button>
            </>
          )}
        </div>

        {/* Price Adjustment Tip */}
        {formData.selectedMainMenu && formData.menu.length > 0 && !editPerHead && (
          <Alert variant="info" className="mt-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <FaInfoCircle className="me-2" />
                <strong>Tip:</strong> You've added {formData.menu.length} extra item(s). 
                Consider increasing the per head price from {formatCurrency(formData.mainMenuPrice)}
              </div>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => setEditPerHead(true)}
              >
                Adjust Price Now
              </Button>
            </div>
          </Alert>
        )}
      </Card.Body>

      {/* Edit Modal for Main Menu Items */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItemIndex !== null ? 'Edit Menu Item' : 'Add New Menu Item'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Item Name</Form.Label>
            <Form.Control
              type="text"
              value={editingItemText}
              onChange={(e) => setEditingItemText(e.target.value)}
              placeholder="Enter item name"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditedItem}>
            {editingItemIndex !== null ? 'Save Changes' : 'Add Item'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default MenuSelection;